import React, { useState, useEffect, useRef, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faEdit, faTrash, faBolt, faArrowLeft, faSave, faSearch, faGlobe, faUser, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import { formatWhatsAppText } from '../../utils';
import EmojiPickerPopover from './EmojiPickerPopover';

const ManageShortcutsModal = ({
  isOpen,
  show,
  onClose,
  commands = [],
  onReload,
  onShortcutAdded,
  onSelectShortcut,
  onSelectCommand
}) => {
  const modalIsOpen = Boolean(isOpen ?? show);
  const handleReload = onReload || onShortcutAdded;
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, idUsuario, nombreCompleto } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState(commands);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'form'
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);

  // Form State
  const [formCommand, setFormCommand] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formText, setFormText] = useState('');
  const [formIsGlobal, setFormIsGlobal] = useState(false);
  const [showModalEmojiPicker, setShowModalEmojiPicker] = useState(false);
  const formTextareaRef = useRef(null);

  const applyFormatToFormText = (prefix, suffix = prefix, defaultPlaceholder = 'texto') => {
    const el = formTextareaRef.current;
    if (!el) return;
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const selected = formText.substring(start, end);
    const content = selected || defaultPlaceholder;
    const replacement = `${prefix}${content}${suffix}`;
    const newText = formText.substring(0, start) + replacement + formText.substring(end);
    setFormText(newText);
    setTimeout(() => {
      el.focus();
      if (selected) {
        el.setSelectionRange(start, start + replacement.length);
      } else {
        el.setSelectionRange(start + prefix.length, start + prefix.length + defaultPlaceholder.length);
      }
    }, 20);
  };

  const insertEmojiToFormText = emoji => {
    const el = formTextareaRef.current;
    if (!el) {
      setFormText(prev => prev + emoji);
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const newText = formText.substring(0, start) + emoji + formText.substring(end);
    setFormText(newText);
    setTimeout(() => {
      el.focus();
      const nextPos = start + emoji.length;
      el.setSelectionRange(nextPos, nextPos);
    }, 20);
  };

  const handleInsertAkasiaPosTemplate = () => {
    const template = `*¡Impulsa tu negocio con Akasia POS!* 🚀💻\n\nCon nuestro sistema punto de venta obtienes:\n✅ *Control total de inventarios* y ventas en tiempo real\n✅ *Facturación electrónica* rápida y sin complicaciones\n✅ *Reportes inteligentes* desde cualquier dispositivo\n✅ *Soporte técnico preferencial*\n\n💳 Planes accesibles y a la medida de tu negocio.\n📲 _¿Te gustaría agendar una demostración gratuita hoy mismo?_`;
    setFormText(template);
    if (!formCommand) {
      setFormCommand('akasiapos');
    }
    if (!formTitle) {
      setFormTitle('Promoción Akasia POS');
    }
  };

  // Sincronizar items iniciales con commands prop cuando no hay búsqueda
  useEffect(() => {
    if (!searchTerm) {
      setItems(commands);
    }
  }, [commands, searchTerm]);

  // Cierre con animación suave
  const handleCloseAnimated = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  // Seleccionar atajo para insertarlo en el chat
  const handleSelectShortcut = (cmd, e) => {
    if (e) e.stopPropagation();
    const text = cmd.vTexto || cmd.text || '';
    if (onSelectShortcut) {
      onSelectShortcut(cmd);
      handleCloseAnimated();
    } else if (onSelectCommand) {
      onSelectCommand(text, false);
      handleCloseAnimated();
    }
  };

  // Cerrar al pulsar Escape
  useEffect(() => {
    if (!modalIsOpen) return;
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        handleCloseAnimated();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalIsOpen, isClosing]);

  // Buscar atajos directamente en la base de datos
  const fetchShortcuts = async (searchQuery = searchTerm) => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (idUsuario) params.append('idUsuario', idUsuario);
      if (searchQuery && searchQuery.trim()) params.append('searchTerm', searchQuery.trim());

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`/api/SupportEngine/GetAtajos${query}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        if (Array.isArray(list)) {
          setItems(list);
        }
      }
    } catch (err) {
      console.error('Error al buscar atajos en BD:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Disparar búsqueda en BD con debounce cuando se escribe
  useEffect(() => {
    if (!modalIsOpen) return;
    const timer = setTimeout(() => {
      fetchShortcuts(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, modalIsOpen, token, idUsuario]);

  if (!modalIsOpen) return null;

  const handleOpenNewForm = () => {
    setEditingId(null);
    setFormCommand('');
    setFormTitle('');
    setFormText('');
    setFormIsGlobal(false);
    setViewMode('form');
  };

  const handleOpenEditForm = cmd => {
    setEditingId(cmd.idAtajo);
    setFormCommand((cmd.vComando || cmd.label || '').replace(/^\//, ''));
    setFormTitle(cmd.vTitulo || cmd.title || '');
    setFormText(cmd.vTexto || cmd.text || '');
    setFormIsGlobal(!cmd.idUsuario);
    setViewMode('form');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setEditingId(null);
  };

  const handleSave = async e => {
    if (e) e.preventDefault();

    let cleanCmd = formCommand.trim();
    if (!cleanCmd) {
      showErrorToast('Por favor escribe un comando para el atajo (ej: /saludo).');
      return;
    }
    if (!cleanCmd.startsWith('/')) {
      cleanCmd = '/' + cleanCmd;
    }
    cleanCmd = cleanCmd.toLowerCase();

    if (!formText.trim()) {
      showErrorToast('Por favor escribe el texto que se insertará con este atajo.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        idAtajo: editingId || null,
        idUsuario: formIsGlobal ? null : (parseInt(idUsuario) || null),
        vComando: cleanCmd,
        vTitulo: formTitle.trim() || cleanCmd,
        vTexto: formText.trim(),
        vCreatedUser: nombreCompleto || 'Agente'
      };

      const response = await fetch('/api/SupportEngine/SaveAtajo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = result.message || 'Error al guardar el atajo';
        if (result.details) {
          try {
            const parsed = typeof result.details === 'string' ? JSON.parse(result.details) : result.details;
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            msg = result.details;
          }
        }
        throw new Error(msg);
      }

      showSuccessToast(editingId ? 'Atajo actualizado con éxito.' : 'Atajo creado con éxito.');
      if (handleReload) await handleReload();
      await fetchShortcuts(searchTerm);
      setViewMode('list');
    } catch (err) {
      showErrorToast(err.message || 'Ocurrió un error al guardar el atajo.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch('/api/SupportEngine/DeleteAtajo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idAtajo: itemToDelete.idAtajo,
          idUsuario: parseInt(idUsuario) || null,
          vUpdatedUser: nombreCompleto || 'Agente'
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = result.message || 'Error al eliminar el atajo';
        if (result.details) {
          try {
            const parsed = typeof result.details === 'string' ? JSON.parse(result.details) : result.details;
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            msg = result.details;
          }
        }
        throw new Error(msg);
      }

      showSuccessToast('Atajo eliminado correctamente.');
      setDeletedIds(prev => [...prev, itemToDelete.idAtajo]);
      setItemToDelete(null);
      if (handleReload) await handleReload();
      await fetchShortcuts(searchTerm);
    } catch (err) {
      showErrorToast(err.message || 'Error al eliminar el atajo.');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredCommands = items.filter(cmd => !deletedIds.includes(cmd.idAtajo));

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      onClick={e => {
        if (e.target === e.currentTarget) handleCloseAnimated();
      }}
      style={{
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        zIndex: 1060,
        animation: `${isClosing ? 'modalBackdropFadeOut' : 'modalBackdropFadeIn'} 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards`
      }}
    >
      <style>{`
        @keyframes modalBackdropFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalBackdropFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes modalDialogScaleIn {
          from {
            opacity: 0;
            transform: scale(0.94) translateY(-14px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes modalDialogScaleOut {
          from {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
          to {
            opacity: 0;
            transform: scale(0.96) translateY(-8px);
          }
        }
      `}</style>

      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        style={{
          animation: `${isClosing ? 'modalDialogScaleOut' : 'modalDialogScaleIn'} 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards`
        }}
      >
        <div
          className="modal-content border-0 shadow-lg"
          style={{
            borderRadius: '14px',
            overflow: 'hidden',
            backgroundColor: isDark ? '#121e2d' : '#ffffff',
            border: isDark ? '1px solid #233142' : 'none',
            color: isDark ? '#f9fafd' : '#0f172a'
          }}
        >
          {/* Header */}
          <div
            className="modal-header px-4 py-3 border-bottom d-flex align-items-center justify-content-between"
            style={{ backgroundColor: isDark ? '#182635' : '#f8fafc', borderColor: isDark ? '#233142' : '#e2e8f0' }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#ecfdf5',
                  color: isDark ? '#34d399' : '#059669'
                }}
              >
                <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.85rem' }} />
              </div>
              <h5 className="modal-title fw-bold mb-0" style={{ fontSize: '1.05rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                {viewMode === 'list' ? 'Catálogo de Atajos Rápidos' : (editingId ? 'Editar Atajo' : 'Crear Nuevo Atajo')}
              </h5>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-link p-1"
              style={{ color: isDark ? '#94a3b8' : '#64748b' }}
              onClick={handleCloseAnimated}
              title="Cerrar"
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '1rem' }} />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body p-4" style={{ maxHeight: '68vh', overflowY: 'auto' }}>
            {viewMode === 'list' ? (
              <>
                {/* Search & Actions Bar */}
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-between align-items-sm-center mb-3">
                  <div className="position-relative flex-grow-1" style={{ maxWidth: '380px' }}>
                    <FontAwesomeIcon
                      icon={isLoading ? faSpinner : faSearch}
                      className={`position-absolute ${isLoading ? 'fa-spin' : ''}`}
                      style={{ top: '10px', left: '12px', fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#64748b' }}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm ps-4 pe-4 rounded-pill shadow-none"
                      style={{
                        backgroundColor: isDark ? '#182635' : '#ffffff',
                        borderColor: isDark ? '#233142' : '#dee2e6',
                        color: isDark ? '#f9fafd' : '#0f172a'
                      }}
                      placeholder="Buscar por comando, título o texto..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-sm p-0 position-absolute border-0 bg-transparent"
                        style={{ top: '6px', right: '12px', color: isDark ? '#94a3b8' : '#64748b' }}
                        onClick={() => setSearchTerm('')}
                      >
                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.75rem' }} />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm text-white d-flex align-items-center gap-2 rounded-pill px-3 shadow-none flex-shrink-0"
                    style={{ backgroundColor: '#065f46', fontWeight: 500 }}
                    onClick={handleOpenNewForm}
                  >
                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.78rem' }} />
                    <span>Nuevo Atajo</span>
                  </button>
                </div>

                {/* List Items */}
                {isLoading && filteredCommands.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-success" role="status" style={{ width: '1.8rem', height: '1.8rem' }} />
                    <p className="small mt-2 mb-0" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Buscando atajos en la base de datos...</p>
                  </div>
                ) : filteredCommands.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-2" style={{ fontSize: '2rem' }}>💬</div>
                    <h6 className="fw-semibold" style={{ color: isDark ? '#f9fafd' : '#475569' }}>No se encontraron atajos</h6>
                    <p className="small mb-3" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      {searchTerm ? 'No hay atajos que coincidan con tu búsqueda.' : 'Aún no hay atajos registrados.'}
                    </p>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success rounded-pill px-3"
                      onClick={handleOpenNewForm}
                    >
                      Crear mi primer atajo
                    </button>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {(onSelectShortcut || onSelectCommand) && (
                      <div
                        className="d-flex align-items-center gap-2 p-2 px-3 rounded-2 mb-1 border"
                        style={{
                          backgroundColor: isDark ? 'rgba(16, 185, 129, 0.08)' : '#f0fdf4',
                          borderColor: isDark ? 'rgba(16, 185, 129, 0.25)' : '#bbf7d0',
                          color: isDark ? '#34d399' : '#15803d',
                          fontSize: '0.78rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.78rem' }} />
                        <span>Haz clic sobre cualquier atajo para insertarlo directamente en el chat.</span>
                      </div>
                    )}
                    {filteredCommands.map((cmd, idx) => {
                      const isGlobal = !cmd.idUsuario;
                      const command = cmd.vComando || cmd.label || '';
                      const title = cmd.vTitulo || cmd.title || command;
                      const text = cmd.vTexto || cmd.text || '';
                      const isSelectable = Boolean(onSelectShortcut || onSelectCommand);

                      return (
                        <div
                          key={cmd.idAtajo || idx}
                          className="card border rounded-3 p-3 transition-all hover-shadow-sm"
                          style={{
                            backgroundColor: isDark ? '#182635' : '#ffffff',
                            borderColor: isDark ? '#233142' : '#e2e8f0',
                            cursor: isSelectable ? 'pointer' : 'default',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={e => {
                            if (isSelectable) {
                              e.currentTarget.style.borderColor = isDark ? '#10b981' : '#059669';
                              e.currentTarget.style.backgroundColor = isDark ? '#1c2d3f' : '#f8fafc';
                            }
                          }}
                          onMouseLeave={e => {
                            if (isSelectable) {
                              e.currentTarget.style.borderColor = isDark ? '#233142' : '#e2e8f0';
                              e.currentTarget.style.backgroundColor = isDark ? '#182635' : '#ffffff';
                            }
                          }}
                          onClick={() => {
                            if (isSelectable) {
                              handleSelectShortcut(cmd);
                            }
                          }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <span
                                  className="badge px-2 py-1"
                                  style={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.82rem',
                                    fontWeight: 600,
                                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#dcfce7',
                                    color: isDark ? '#34d399' : '#15803d',
                                    border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #bbf7d0'
                                  }}
                                >
                                  {command.startsWith('/') ? command : `/${command}`}
                                </span>
                                <span className="fw-bold" style={{ fontSize: '0.9rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                  {title}
                                </span>
                                {isGlobal ? (
                                  <span
                                    className="badge border"
                                    style={{
                                      fontSize: '0.68rem',
                                      backgroundColor: isDark ? 'rgba(14, 165, 233, 0.15)' : '#e0f2fe',
                                      color: isDark ? '#38bdf8' : '#0284c7',
                                      borderColor: isDark ? 'rgba(14, 165, 233, 0.3)' : '#bae6fd'
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faGlobe} className="me-1" /> Global
                                  </span>
                                ) : (
                                  <span
                                    className="badge border"
                                    style={{
                                      fontSize: '0.68rem',
                                      backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#e6fcf5',
                                      color: isDark ? '#34d399' : '#0ca678',
                                      borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : '#c3fae8'
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faUser} className="me-1" /> {cmd.vCreatedUser || 'Agente'}
                                  </span>
                                )}
                              </div>
                              <p
                                className="small mb-0 text-break"
                                style={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap', lineHeight: 1.4, color: isDark ? '#cbd5e1' : '#475569' }}
                              >
                                {formatWhatsAppText(text)}
                              </p>
                            </div>

                            {/* Actions */}
                            <div className="d-flex align-items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                              <button
                                type="button"
                                className="btn btn-sm border p-1 px-2 rounded-2"
                                style={{
                                  backgroundColor: isDark ? '#121e2d' : '#f8fafc',
                                  borderColor: isDark ? '#233142' : '#e2e8f0',
                                  color: isDark ? '#94a3b8' : '#64748b'
                                }}
                                title="Editar atajo"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleOpenEditForm(cmd);
                                }}
                              >
                                <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.78rem' }} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm border p-1 px-2 rounded-2 text-danger"
                                style={{
                                  backgroundColor: isDark ? '#121e2d' : '#f8fafc',
                                  borderColor: isDark ? '#233142' : '#e2e8f0'
                                }}
                                title="Eliminar atajo"
                                disabled={isDeleting}
                                onClick={e => {
                                  e.stopPropagation();
                                  setItemToDelete(cmd);
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.78rem' }} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              /* Form View */
              <form onSubmit={handleSave}>
                <div className="mb-3">
                  <label className="form-label fw-semibold small mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                    Comando rápido <span className="text-danger">*</span>
                  </label>
                  <div className="input-group input-group-sm">
                    <span
                      className="input-group-text fw-bold"
                      style={{
                        backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                        borderColor: isDark ? '#233142' : '#ced4da',
                        color: isDark ? '#38bdf8' : '#64748b'
                      }}
                    >
                      /
                    </span>
                    <input
                      type="text"
                      className="form-control shadow-none font-monospace"
                      placeholder="ej: saludo, garantia, precio..."
                      value={formCommand}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
                          e.preventDefault();
                          setFormCommand(prev => (prev && !prev.endsWith('_') ? `${prev}_` : prev));
                        }
                      }}
                      onChange={e => {
                        const clean = e.target.value.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase();
                        setFormCommand(clean);
                      }}
                      required
                      autoFocus
                      style={{
                        backgroundColor: isDark ? '#182635' : '#ffffff',
                        borderColor: isDark ? '#233142' : '#ced4da',
                        color: isDark ? '#f9fafd' : '#0f172a'
                      }}
                    />
                  </div>
                  <small style={{ fontSize: '0.73rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Sin espacios. Usa Enter o Espacio para separar con guion bajo (<span className="fw-semibold">_</span>).
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                    Título o Descripción <span className="small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm shadow-none"
                    placeholder="ej: Saludo de bienvenida"
                    value={formTitle}
                    onChange={e => setFormTitle(e.target.value)}
                    style={{
                      backgroundColor: isDark ? '#182635' : '#ffffff',
                      borderColor: isDark ? '#233142' : '#ced4da',
                      color: isDark ? '#f9fafd' : '#0f172a'
                    }}
                  />
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label fw-semibold small mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                      Texto del Mensaje <span className="text-danger">*</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success py-0 px-2 rounded-pill shadow-none"
                      style={{ fontSize: '0.73rem', fontWeight: 600 }}
                      onClick={handleInsertAkasiaPosTemplate}
                      title="Insertar plantilla de promoción para Akasia POS"
                    >
                      🌟 Plantilla Akasia POS
                    </button>
                  </div>

                  {/* Barra de formato y emojis rápidos */}
                  <div
                    className="p-1 px-2 border rounded-top d-flex align-items-center justify-content-between flex-wrap gap-1"
                    style={{
                      backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                      borderColor: isDark ? '#233142' : '#ced4da',
                      borderBottom: 'none'
                    }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-sm py-0 px-2 rounded border-0 fw-bold"
                        style={{
                          backgroundColor: isDark ? '#0f172a' : '#ffffff',
                          color: isDark ? '#f9fafd' : '#0f172a',
                          height: '24px',
                          minWidth: '24px'
                        }}
                        title="Negrita (*texto*)"
                        onClick={() => applyFormatToFormText('*', '*', 'negrita')}
                      >
                        B
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm py-0 px-2 rounded border-0 fst-italic"
                        style={{
                          fontFamily: 'serif',
                          backgroundColor: isDark ? '#0f172a' : '#ffffff',
                          color: isDark ? '#f9fafd' : '#0f172a',
                          height: '24px',
                          minWidth: '24px'
                        }}
                        title="Cursiva (_texto_)"
                        onClick={() => applyFormatToFormText('_', '_', 'cursiva')}
                      >
                        I
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm py-0 px-2 rounded border-0 text-decoration-line-through"
                        style={{
                          backgroundColor: isDark ? '#0f172a' : '#ffffff',
                          color: isDark ? '#f9fafd' : '#0f172a',
                          height: '24px',
                          minWidth: '24px'
                        }}
                        title="Tachado (~texto~)"
                        onClick={() => applyFormatToFormText('~', '~', 'tachado')}
                      >
                        S
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm py-0 px-2 rounded border-0 font-monospace"
                        style={{
                          backgroundColor: isDark ? '#0f172a' : '#ffffff',
                          color: isDark ? '#f9fafd' : '#0f172a',
                          fontSize: '0.72rem',
                          height: '24px',
                          minWidth: '24px'
                        }}
                        title="Monoespaciado (```texto```)"
                        onClick={() => applyFormatToFormText('```', '```', 'código')}
                      >
                        &lt;/&gt;
                      </button>
                    </div>

                    {/* Iconos / Emojis promocionales */}
                    <div className="d-flex align-items-center gap-1 position-relative">
                      {['🚀', '💻', '🛒', '💳', '📦', '⭐', '✅', '💡', '📞', '🏷️', '🔥', '💬', '📲', '✨'].map((emoji, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="btn btn-sm p-0 border-0"
                          style={{ fontSize: '0.95rem', lineHeight: 1 }}
                          onClick={() => insertEmojiToFormText(emoji)}
                          title={`Insertar ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                      <div className="vr mx-1" style={{ height: '14px', opacity: 0.3 }} />
                      <button
                        type="button"
                        className="btn btn-sm p-0 px-1 border-0 rounded"
                        style={{
                          fontSize: '0.95rem',
                          backgroundColor: showModalEmojiPicker ? (isDark ? '#334155' : '#e2e8f0') : 'transparent'
                        }}
                        onClick={() => setShowModalEmojiPicker(!showModalEmojiPicker)}
                        title="Ver más emojis e iconos de WhatsApp"
                      >
                        😊
                      </button>

                      {showModalEmojiPicker && (
                        <div
                          className="position-absolute shadow-lg"
                          style={{
                            top: '100%',
                            right: '0',
                            marginTop: '4px',
                            zIndex: 1070
                          }}
                        >
                          <EmojiPickerPopover
                            onSelectEmoji={insertEmojiToFormText}
                            onClose={() => setShowModalEmojiPicker(false)}
                            isDark={isDark}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <textarea
                    ref={formTextareaRef}
                    rows={5}
                    className="form-control shadow-none rounded-0 rounded-bottom"
                    placeholder="Escribe aquí el texto formateado (*negrita*, _cursiva_, saltos de línea)..."
                    value={formText}
                    onChange={e => setFormText(e.target.value)}
                    required
                    style={{
                      fontSize: '0.85rem',
                      backgroundColor: isDark ? '#182635' : '#ffffff',
                      borderColor: isDark ? '#233142' : '#ced4da',
                      color: isDark ? '#f9fafd' : '#0f172a'
                    }}
                  />

                  {/* Vista previa en tiempo real estilo WhatsApp */}
                  {formText.trim() && (
                    <div className="mt-2 p-2 rounded-3 border" style={{ backgroundColor: isDark ? '#0f172a' : '#f8fafc', borderColor: isDark ? '#233142' : '#e2e8f0' }}>
                      <div className="d-flex align-items-center justify-content-between mb-1 px-1">
                        <span className="fw-semibold text-success" style={{ fontSize: '0.72rem' }}>
                          Vista previa de cómo lo verá el cliente en WhatsApp:
                        </span>
                        <span className="text-muted" style={{ fontSize: '0.68rem' }}>
                          Formato en vivo
                        </span>
                      </div>
                      <div
                        className="p-3 shadow-sm"
                        style={{
                          backgroundColor: isDark ? '#005c4b' : '#d9fdd3',
                          color: isDark ? '#e9edef' : '#111b21',
                          borderRadius: '8px 0px 8px 8px',
                          fontSize: '0.86rem',
                          lineHeight: '1.4'
                        }}
                      >
                        <div className="text-break" style={{ whiteSpace: 'pre-wrap' }}>
                          {formatWhatsAppText(formText)}
                        </div>
                        <div className="d-flex align-items-center justify-content-end gap-1 mt-1" style={{ fontSize: '0.65rem', color: isDark ? '#8696a0' : '#64748b' }}>
                          <span>{nombreCompleto || 'Tú'}</span>
                          <span>•</span>
                          <span>Ahora</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input shadow-none"
                    id="checkIsGlobal"
                    checked={formIsGlobal}
                    onChange={e => setFormIsGlobal(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="checkIsGlobal" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                    Hacer este atajo <strong>global</strong> para todos los agentes de soporte
                  </label>
                </div>

                {/* Form Buttons */}
                <div
                  className="d-flex justify-content-between align-items-center pt-2 border-top"
                  style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                >
                  <button
                    type="button"
                    className="btn btn-sm px-3 shadow-none fw-semibold d-flex align-items-center gap-1.5"
                    style={{
                      fontSize: '0.82rem',
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      color: isDark ? '#38bdf8' : '#0284c7',
                      border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
                      transition: 'all 0.15s ease'
                    }}
                    onClick={handleBackToList}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '0.75rem' }} />
                    <span>Volver a la lista</span>
                  </button>

                  <button
                    type="submit"
                    className="btn btn-sm text-white rounded-pill px-4 shadow-none d-flex align-items-center gap-2"
                    style={{ backgroundColor: '#065f46' }}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <div className="spinner-border spinner-border-sm text-white" role="status" />
                        <span>Guardando...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} style={{ fontSize: '0.8rem' }} />
                        <span>{editingId ? 'Guardar Cambios' : 'Crear Atajo'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmación personalizado para eliminar atajo */}
      {itemToDelete && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1070 }}
          onClick={() => { if (!isDeleting) setItemToDelete(null); }}
        >
          <div
            className="card border-0 shadow-lg p-4 text-center"
            style={{
              maxWidth: '410px',
              width: '92%',
              borderRadius: '16px',
              backgroundColor: isDark ? '#121e2d' : '#ffffff',
              border: isDark ? '1px solid #233142' : 'none',
              color: isDark ? '#f9fafd' : '#0f172a',
              animation: 'modalDialogScaleIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-3"
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                color: isDark ? '#f87171' : '#dc2626'
              }}
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '1.2rem' }} />
            </div>

            <h5 className="fw-bold mb-2" style={{ fontSize: '1.1rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
              ¿Eliminar atajo?
            </h5>

            <p className="small mb-4" style={{ lineHeight: 1.45, color: isDark ? '#94a3b8' : '#64748b' }}>
              ¿Estás seguro de que deseas eliminar el comando{' '}
              <strong style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{itemToDelete.vComando || itemToDelete.label}</strong>?
              <br />
              <span style={{ fontSize: '0.75rem', opacity: 0.85 }}>Esta acción no se puede deshacer.</span>
            </p>

            <div className="d-flex justify-content-center gap-2">
              <button
                type="button"
                className="btn btn-sm px-3 shadow-none fw-semibold"
                style={{
                  fontSize: '0.82rem',
                  backgroundColor: isDark ? '#1e293b' : '#ffffff',
                  color: isDark ? '#38bdf8' : '#0284c7',
                  border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
                  transition: 'all 0.15s ease'
                }}
                disabled={isDeleting}
                onClick={() => setItemToDelete(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger px-4 rounded-pill shadow-none fw-semibold d-flex align-items-center gap-2"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
              >
                {isDeleting ? (
                  <>
                    <div className="spinner-border spinner-border-sm text-white" role="status" />
                    <span>Eliminando...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.78rem' }} />
                    <span>Eliminar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageShortcutsModal;
